import {
	Component,
	Input,
	Output,
	EventEmitter,
	OnInit,
	OnDestroy,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	forwardRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";

// Models
import { UbicacionEspanaViewModel } from "src/app/_core/models/centros/cebe-contract-list-base.model";

// Services
import { MasterService } from "src/app/_core/providers/master.service";
import { NotificationService } from "src/app/_core/providers/notification.service";

@Component({
    selector: "app-ubicacion-espana-autocomplete",
    templateUrl: "./ubicacion-espana-autocomplete.component.html",
    styleUrls: ["./ubicacion-espana-autocomplete.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UbicacionEspanaAutocompleteComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class UbicacionEspanaAutocompleteComponent
	implements OnInit, OnDestroy, ControlValueAccessor
{
	private readonly destroy$ = new Subject<void>();

	// Inputs
	@Input() label = "Ubicación España";
	@Input() placeholder =
		"Buscar ubicación en España por código o descripción";
	@Input() required = false;
	@Input() disabled = false;
	@Input() hint = "Busque por código o descripción de ubicación en España";

	// Outputs
	@Output() ubicacionEspanaSelected =
		new EventEmitter<UbicacionEspanaViewModel>();

	// State
	ubicacionesEspana: UbicacionEspanaViewModel[] = [];
	ubicacionEspanaId: number | null = null;
	selectedUbicacionEspana: UbicacionEspanaViewModel | null = null;

	// ControlValueAccessor
	private onChange = (_value: number | null): void => {
		// This is intentional
	};
	private onTouched = (): void => {
		// This is intentional
	};

	constructor(
		private readonly masterService: MasterService,
		private readonly notificationService: NotificationService,
		private readonly cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.loadUbicacionesEspana();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// ControlValueAccessor implementation
	writeValue(value: number | null): void {
		this.ubicacionEspanaId = value ?? null;
		this.syncSelected();
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: number | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}

	/**
	 * Carga la lista de ubicaciones en España
	 */
	private loadUbicacionesEspana(): void {
		this.masterService
			.getUbicacionEspanaList()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (ubicacionesEspana) => {
					this.ubicacionesEspana = ubicacionesEspana;
					this.syncSelected();
					this.cdr.markForCheck();
				},
				error: () => {
					this.notificationService.showError(
						"Error al cargar las ubicaciones en España",
					);
				},
			});
	}

	/** Recalcula el objeto seleccionado a partir del id actual. */
	private syncSelected(): void {
		this.selectedUbicacionEspana =
			this.ubicacionEspanaId != null
				? (this.ubicacionesEspana.find(
						(u) => u.id === this.ubicacionEspanaId,
					) ?? null)
				: null;
	}

	/**
	 * Obtiene el texto mostrado para una ubicación en España
	 */
	displayUbicacionEspana = (
		ubicacionEspana: UbicacionEspanaViewModel | string,
	): string => {
		if (typeof ubicacionEspana === "string") {
			return ubicacionEspana;
		}
		return ubicacionEspana &&
			ubicacionEspana.codigo &&
			ubicacionEspana.descripcion
			? `${ubicacionEspana.codigo} - ${ubicacionEspana.descripcion}`
			: "";
	};

	/** Selección desde el autocomplete `.ds`. */
	onValueChange(ubicacionEspana: UbicacionEspanaViewModel | null): void {
		this.ubicacionEspanaId = ubicacionEspana ? ubicacionEspana.id : null;
		this.selectedUbicacionEspana = ubicacionEspana;
		this.onChange(this.ubicacionEspanaId);
		this.onTouched();
		if (ubicacionEspana) {
			this.ubicacionEspanaSelected.emit(ubicacionEspana);
		}
		this.cdr.markForCheck();
	}

	onControlTouched(): void {
		this.onTouched();
	}
}
